import React, { useState, useRef } from 'react';
import { 
  Upload, Link, FileText, Image, Loader2, Check, X, 
  Download, RefreshCw, Sparkles, Globe, File
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Import Component for Listings
 * Auto-extracts product data from URLs, images, PDFs, and documents
 */
const AIImportWidget = ({ onDataExtracted, listingType = 'product' }) => {
  const [importMode, setImportMode] = useState('url'); // url, file
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  
  const handleUrlImport = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    setLoading(true);
    setError(null);
    setExtractedData(null);
    
    try {
      const response = await fetch(`${API_URL}/api/import/from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setExtractedData(result.data);
        toast.success('Data extracted successfully!');
        if (onDataExtracted) {
          onDataExtracted(result.data);
        }
      } else {
        throw new Error(result.detail || 'Failed to extract data');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
    
    setLoading(false);
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 
                          'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.pdf', '.doc', '.docx'];
    
    const isValidType = allowedTypes.some(t => file.type.includes(t.split('/')[1]));
    const isValidExt = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!isValidType && !isValidExt) {
      toast.error('Unsupported file type. Use: JPG, PNG, PDF, DOC, DOCX');
      return;
    }
    
    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setExtractedData(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('download_images', 'true');
      
      const response = await fetch(`${API_URL}/api/import/from-file`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        setExtractedData(result.data);
        toast.success('Data extracted successfully!');
        if (onDataExtracted) {
          onDataExtracted(result.data);
        }
      } else {
        throw new Error(result.detail || 'Failed to extract data');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
    
    setLoading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const applyExtractedData = () => {
    if (extractedData && onDataExtracted) {
      onDataExtracted(extractedData);
      toast.success('Data applied to form!');
    }
  };
  
  const clearData = () => {
    setExtractedData(null);
    setError(null);
    setUrl('');
  };
  
  return (
    <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-2xl p-6 border border-purple-500/30 mb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <Sparkles className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">AI Auto-Import</h3>
          <p className="text-gray-400 text-sm">Extract {listingType} data from URL, image, or document</p>
        </div>
      </div>
      
      {/* Import Mode Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setImportMode('url')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            importMode === 'url' 
              ? 'bg-purple-500 text-white' 
              : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
          }`}
        >
          <Globe className="w-4 h-4" />
          From URL
        </button>
        <button
          onClick={() => setImportMode('file')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            importMode === 'file' 
              ? 'bg-purple-500 text-white' 
              : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
          }`}
        >
          <Upload className="w-4 h-4" />
          Upload File
        </button>
      </div>
      
      {/* URL Import */}
      {importMode === 'url' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste Amazon, Flipkart, or any product URL..."
              className="flex-1 bg-slate-800 text-white px-4 py-3 rounded-lg border border-slate-700 focus:border-purple-500 focus:outline-none"
              disabled={loading}
            />
            <button
              onClick={handleUrlImport}
              disabled={loading || !url.trim()}
              className="px-6 py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              Extract
            </button>
          </div>
          <p className="text-gray-500 text-xs">
            Supports: Amazon, Flipkart, Myntra, Meesho, and any product page
          </p>
        </div>
      )}
      
      {/* File Upload */}
      {importMode === 'file' && (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
            className="hidden"
            disabled={loading}
          />
          <div 
            onClick={() => !loading && fileInputRef.current?.click()}
            className={`border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500 transition-all ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-12 h-12 text-purple-400 animate-spin" />
                <p className="text-gray-400">Analyzing file with AI...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="flex gap-4">
                  <Image className="w-10 h-10 text-blue-400" />
                  <FileText className="w-10 h-10 text-red-400" />
                  <File className="w-10 h-10 text-green-400" />
                </div>
                <p className="text-white font-medium">Click to upload or drag & drop</p>
                <p className="text-gray-500 text-sm">JPG, PNG, PDF, DOC, DOCX (max 10MB)</p>
              </div>
            )}
          </div>
          <p className="text-gray-500 text-xs">
            Upload product photos, WhatsApp images, catalogs, invoices, or spec sheets
          </p>
        </div>
      )}
      
      {/* Error Display */}
      {error && (
        <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start gap-3">
          <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium">Extraction Failed</p>
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        </div>
      )}
      
      {/* Extracted Data Preview */}
      {extractedData && (
        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-medium">Data Extracted Successfully!</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={applyExtractedData}
                className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-400 transition-all flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Apply to Form
              </button>
              <button
                onClick={clearData}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-600 transition-all"
              >
                Clear
              </button>
            </div>
          </div>
          
          {/* Data Preview */}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {extractedData.name && (
              <div>
                <p className="text-gray-500 text-xs">Name</p>
                <p className="text-white font-medium truncate">{extractedData.name}</p>
              </div>
            )}
            {extractedData.price && (
              <div>
                <p className="text-gray-500 text-xs">Price</p>
                <p className="text-green-400 font-bold">₹{extractedData.price}</p>
              </div>
            )}
            {extractedData.brand && (
              <div>
                <p className="text-gray-500 text-xs">Brand</p>
                <p className="text-white">{extractedData.brand}</p>
              </div>
            )}
            {extractedData.category && (
              <div>
                <p className="text-gray-500 text-xs">Category</p>
                <p className="text-white">{extractedData.category}</p>
              </div>
            )}
            {extractedData.description && (
              <div className="md:col-span-2">
                <p className="text-gray-500 text-xs">Description</p>
                <p className="text-gray-300 text-sm line-clamp-2">{extractedData.description}</p>
              </div>
            )}
            {extractedData.specifications && extractedData.specifications.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-gray-500 text-xs mb-1">Specifications</p>
                <div className="flex flex-wrap gap-2">
                  {extractedData.specifications.slice(0, 5).map((spec, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-800 text-gray-300 rounded text-xs">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {extractedData.images && extractedData.images.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-gray-500 text-xs mb-1">Images Found: {extractedData.images.length}</p>
                <div className="flex gap-2 overflow-x-auto">
                  {extractedData.images.slice(0, 4).map((img, i) => (
                    <img 
                      key={i} 
                      src={img} 
                      alt={`Product ${i+1}`}
                      className="w-16 h-16 object-cover rounded-lg border border-slate-700"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIImportWidget;
